import { createTemplate, type ReportTemplate } from "@broadpaper/core";
import {
  createTemplateAiRequest,
  runTemplateAi,
  REFUSAL_MESSAGE,
  TemplateAiError,
  type TemplateAiAllowance,
  type TemplateAiErrorKind,
  type TemplateAiMode
} from "@broadpaper/core/ai";
import { Observable } from "./observable.js";
import type { EditorSession } from "./session.js";

export type AiStatus =
  | { phase: "idle" }
  | { phase: "running"; attempt: number; mode: TemplateAiMode }
  | {
      phase: "proposal";
      requestId: string;
      mode: TemplateAiMode;
      template: ReportTemplate;
      notes?: string;
      /**
       * The document as it was when the request went out. Accept compares the
       * store against it by identity: any edit since makes a new template object,
       * and replacing that would throw the edit away without a word.
       */
      base: ReportTemplate;
    }
  | { phase: "refused"; message: string }
  | { phase: "error"; kind: TemplateAiErrorKind; message: string; hint?: string; action?: { label: string; href: string } };

export interface AiState {
  mode: TemplateAiMode;
  instruction: string;
  status: AiStatus;
  allowance: TemplateAiAllowance | null;
}

/**
 * The designer's side of AI template design, framework-neutral like the rest
 * of the session.
 *
 * It asks, it holds what came back, and it applies it only when told to — as
 * one `replaceTemplate` command through the store, so an accepted proposal is
 * a single entry in undo like any other edit, and a discarded one never touched
 * the document at all. Everything about the model is the host's provider.
 */
export class AiController {
  readonly state = new Observable<AiState>({ mode: "update", instruction: "", status: { phase: "idle" }, allowance: null });
  private controller: AbortController | null = null;
  private allowanceSeq = 0;

  constructor(private readonly session: EditorSession) {}

  get provider() {
    return this.session.options.get().ai;
  }

  setMode(mode: TemplateAiMode): void {
    this.state.patch({ mode });
  }

  setInstruction(instruction: string): void {
    this.state.patch({ instruction });
  }

  async refreshAllowance(): Promise<void> {
    const provider = this.provider;
    if (!provider?.allowance) return;
    const seq = ++this.allowanceSeq;
    try {
      const allowance = await provider.allowance();
      // A slow answer to an older question must not overwrite a newer one.
      if (seq === this.allowanceSeq) this.state.patch({ allowance });
    } catch {
      /* A bar that could not be drawn is not worth an error in front of the person. */
    }
  }

  async generate(): Promise<void> {
    const provider = this.provider;
    if (!provider || this.state.get().status.phase === "running") return;
    const { mode, instruction } = this.state.get();
    const store = this.session.store;
    const base = store.template;
    // A new report keeps the design's identity and paper and nothing else: the
    // host stores it under its id, and the page size is usually a decision the
    // person has already made. Everything they drew is what "new" replaces.
    const current =
      mode === "create"
        ? createTemplate({ id: base.id, name: base.name, page: base.page, locale: base.settings?.locale ?? this.session.options.get().locale, currency: base.settings?.currency ?? this.session.options.get().currency })
        : base;
    const theme = this.session.activeTheme;
    const dataSources = this.session.dataSources;

    let built: ReturnType<typeof createTemplateAiRequest>;
    try {
      built = createTemplateAiRequest({
        mode,
        instruction,
        registry: this.session.registry,
        dataSources,
        theme,
        current,
        selection: mode === "update" ? store.getState().selection : undefined,
        locale: this.session.options.get().locale
      });
    } catch (e) {
      this.fail(e);
      return;
    }

    const controller = new AbortController();
    this.controller = controller;
    this.state.patch({ status: { phase: "running", attempt: 0, mode } });
    try {
      const result = await runTemplateAi({
        request: built.request,
        assets: built.assets,
        registry: this.session.registry,
        dataSources,
        theme,
        current,
        signal: controller.signal,
        maxRepairs: provider.maxRepairs,
        generate: (request, signal) => provider.generate(request, signal),
        onAttempt: (attempt) => this.state.patch({ status: { phase: "running", attempt, mode } })
      });
      if (this.controller !== controller) return;
      if (result.status === "refused") this.state.patch({ status: { phase: "refused", message: REFUSAL_MESSAGE } });
      else this.state.patch({ status: { phase: "proposal", requestId: result.requestId, mode, template: result.template, notes: result.notes, base } });
    } catch (e) {
      if (this.controller === controller) this.fail(e);
    } finally {
      if (this.controller === controller) this.controller = null;
      void this.refreshAllowance();
    }
  }

  cancel(): void {
    this.controller?.abort();
    this.controller = null;
    this.state.patch({ status: { phase: "idle" } });
  }

  /** Whether the document has changed since the proposal was asked for. */
  isStale(): boolean {
    const s = this.state.get().status;
    return s.phase === "proposal" && this.session.store.template !== s.base;
  }

  /** Applies the proposal as one undoable step. False, and nothing done, if the design moved on. */
  accept(): boolean {
    const s = this.state.get().status;
    if (s.phase !== "proposal" || this.isStale()) return false;
    this.session.store.dispatch({ type: "replaceTemplate", template: s.template }, { label: s.mode === "create" ? "New report from AI" : "AI changes" });
    this.provider?.decided?.(s.requestId, "applied");
    this.state.patch({ status: { phase: "idle" }, instruction: "" });
    return true;
  }

  discard(): void {
    const s = this.state.get().status;
    if (s.phase === "proposal") this.provider?.decided?.(s.requestId, "rejected");
    this.state.patch({ status: { phase: "idle" } });
  }

  /** Back to the instruction, keeping it, after an answer the person does not want. */
  dismiss(): void {
    this.state.patch({ status: { phase: "idle" } });
  }

  destroy(): void {
    this.controller?.abort();
    this.controller = null;
  }

  private fail(e: unknown): void {
    if (e instanceof TemplateAiError) {
      if (e.kind === "cancelled") {
        this.state.patch({ status: { phase: "idle" } });
        return;
      }
      if (e.kind === "refused") {
        this.state.patch({ status: { phase: "refused", message: REFUSAL_MESSAGE } });
        return;
      }
      this.state.patch({ status: { phase: "error", kind: e.kind, message: e.message, hint: e.hint, action: e.action } });
      return;
    }
    this.state.patch({ status: { phase: "error", kind: "failed", message: "Something went wrong while designing that. Please try again." } });
  }
}
